import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Calendar, MapPin, Users, TrendingUp } from 'lucide-react';
import { Company } from '@/pages/Companies';
import * as CapLedgerService from '@/services/capLedgerService';

interface CompanyOverviewProps {
  company: Company;
  capTable: CapLedgerService.CapTableSummary[];
}

export const CompanyOverview = ({ company, capTable }: CompanyOverviewProps) => {
  const totalShares = capTable.reduce((sum, item) => sum + Number(item.total_shares || 0), 0);
  const shareholderCount = capTable.length;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Shares</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalShares.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Issued shares</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Shareholders</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{shareholderCount}</div>
            <p className="text-xs text-muted-foreground">Total stakeholders</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Jurisdiction</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{company.jurisdiction || 'N/A'}</div>
            <p className="text-xs text-muted-foreground">Legal jurisdiction</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Incorporated</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {company.incorporation_date
                ? new Date(company.incorporation_date).getFullYear()
                : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">Year of incorporation</p>
          </CardContent>
        </Card>
      </div>

      {/* Company Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Company Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Company Name</p>
              <p className="text-base font-semibold">{company.name}</p>
            </div>

            {company.registration_number && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Registration Number</p>
                <p className="text-base font-semibold">{company.registration_number}</p>
              </div>
            )}

            {company.incorporation_date && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Incorporation Date</p>
                <p className="text-base font-semibold">
                  {new Date(company.incorporation_date).toLocaleDateString()}
                </p>
              </div>
            )}

            {company.jurisdiction && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Jurisdiction</p>
                <p className="text-base font-semibold">{company.jurisdiction}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
